import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../utils/siteConfig';
import { StructuredData } from '../components/StructuredData';

import {
  Terminal,
  ChevronRight,
  Bookmark,
  Briefcase,
  GraduationCap,
  TrendingUp,
  Brain,
  Clock,
  CheckCircle,
  Code,
  Shield,
  BarChart3,
  Building2,
  ExternalLink,
  Download,
  Star,
  BookOpen,
  Cpu,
  Database,
  Bot,
} from 'lucide-react';
import { MAJORS_DATA, generateSlug, RIASEC_MAP, MajorDetail } from '../data/majorsData';
import { apiService, CareerOrientationGroup } from '@vsat/shared';

export default function CareerExplorationPage() {
  const [searchParams] = useSearchParams();
  const careerSlug = searchParams.get('career') || 'cong-nghe-thong-tin';
  const [isSaved, setIsSaved] = useState(false);

  const [career, setCareer] = useState<(MajorDetail & { slug: string }) | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCareer = async () => {
      setLoading(true);
      // 1. Check static data
      if (MAJORS_DATA[careerSlug]) {
        setCareer({ ...MAJORS_DATA[careerSlug], slug: careerSlug });
        setLoading(false);
        return;
      }

      // 2. Fetch from API
      try {
        const groups = await apiService.getCareerOrientationPublicGroups();
        let foundMajorName = '';
        let foundGroupId = '';
        let foundGroup: CareerOrientationGroup | undefined;

        for (const group of groups) {
          // Search in thptMajorGroups
          if (group.thptMajorGroups) {
            for (const majorGroup of group.thptMajorGroups) {
              const match = (majorGroup.majors as string[]).find(
                name => generateSlug(name) === careerSlug
              );
              if (match) {
                foundMajorName = match;
                foundGroupId = group.code;
                foundGroup = group;
                break;
              }
            }
          }
          if (foundMajorName) break;

          // Search in thcsCareerGroups
          if (group.thcsCareerGroups) {
            for (const careerGroup of group.thcsCareerGroups) {
              const match = (careerGroup.careers as string[]).find(
                name => generateSlug(name) === careerSlug
              );
              if (match) {
                foundMajorName = match;
                foundGroupId = group.code;
                foundGroup = group;
                break;
              }
            }
          }
          if (foundMajorName) break;
        }

        if (foundMajorName && foundGroupId && foundGroup) {
          const riasecInfo = RIASEC_MAP[foundGroupId];
          // Construct object
          setCareer({
            slug: careerSlug,
            code: '---',
            name: foundMajorName,
            category: riasecInfo.category,
            examBlocks: 'A00, A01, D01, ...',
            icon: riasecInfo.icon,
            salary: 'Đang cập nhật',
            riasec: `${riasecInfo.code} (${riasecInfo.name})`,
            employmentRate: 'Cao',
            overview:
              foundGroup.fullDesc || `Ngành ${foundMajorName} thuộc nhóm ${riasecInfo.name}.`,
            subjects: ['Toán', 'Văn', 'Anh'],
            softSkills: ['Giao tiếp', 'Làm việc nhóm', 'Tư duy logic'],
            traits: foundGroup.shortDescPoints || [],
            jobs: [
              {
                title: `Cử nhân ${foundMajorName}`,
                desc: `Làm việc trong lĩnh vực ${foundMajorName.toLowerCase()}`,
                icon: riasecInfo.icon,
              },
            ],
            universities: [
              {
                name: 'Đang cập nhật',
                abbr: 'N/A',
                region: 'Toàn quốc',
                note: 'Tham khảo quy chế tuyển sinh',
              },
            ],
            relatedCareers: [],
            hasFullDetails: false,
          });
        } else {
          setCareer(null);
        }
      } catch (e) {
        console.error(e);
        setCareer(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCareer();
  }, [careerSlug]);

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-background-dark text-gray-900 dark:text-white font-body flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 pt-14 pb-12 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent'></div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // If major not found, redirect to majors list
  if (!career) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-background-dark text-gray-900 dark:text-white font-body flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 pt-14 pb-12 flex items-center justify-center'>
          <div className='text-center'>
            <h1 className='text-2xl font-bold mb-4'>Không tìm thấy ngành học</h1>
            <Link to='/nganh-hoc' className='text-primary hover:underline'>
              ← Quay lại danh sách ngành học
            </Link>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  const iconMap: Record<string, any> = {
    terminal: Terminal,
    code: Code,
    shield: Shield,
    analytics: BarChart3,
    memory: Cpu,
    database: Database,
    smart_toy: Bot,
  };

  const CareerIcon = iconMap[career.icon] || Terminal;

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-background-dark text-gray-900 dark:text-white font-body flex flex-col'>
      <SEOHead
        title={`${career.name} - Thông tin ngành học, cơ hội việc làm | THPT Online`}
        description={`Tìm hiểu chi tiết về ngành ${career.name}: mức lương ${career.salary}, khối thi ${career.examBlocks}, cơ hội việc làm và các trường đại học đào tạo.`}
        keywords={`${career.name}, ngành ${career.category}, ${career.riasec}, khối thi ${career.examBlocks}, việc làm ngành ${career.name}`}
        canonicalUrl={getCanonicalUrl(`/kham-pha-nghe-nghiep?career=${careerSlug}`)}
      />
      <StructuredData
        id={`breadcrumb-career-${careerSlug}`}
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Danh sách ngành học', url: getCanonicalUrl('/nganh-hoc') },
            {
              name: career.name,
              url: getCanonicalUrl(`/kham-pha-nghe-nghiep?career=${careerSlug}`),
            },
          ],
        }}
      />
      <UnifiedHeader />

      <main className='flex-1 pt-20 pb-12 lg:pt-24'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6'>
          {/* Breadcrumbs */}
          <div className='flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400'>
            <Link to='/' className='hover:text-primary transition-colors'>
              Trang chủ
            </Link>
            <ChevronRight className='w-4 h-4' />
            <Link to='/nganh-hoc' className='hover:text-primary transition-colors'>
              Danh sách ngành học
            </Link>
            <ChevronRight className='w-4 h-4' />
            <span className='font-medium text-gray-900 dark:text-white'>Chi tiết ngành</span>
          </div>

          {/* Demo Note Banner */}
          <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3'>
            <div className='p-1.5 bg-amber-100 dark:bg-amber-800/50 rounded-full text-amber-600 dark:text-amber-400 shrink-0'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='w-5 h-5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div>
              <p className='text-sm font-semibold text-amber-800 dark:text-amber-300'>
                Phiên bản trải nghiệm sớm
              </p>
              <p className='text-xs text-amber-700 dark:text-amber-400 mt-0.5'>
                Đây là phiên bản demo với dữ liệu mẫu. Kho dữ liệu ngành nghề chuyên sâu đang được
                chúng tôi hoàn thiện và cập nhật liên tục. Hãy quay lại sớm để khám phá thông tin
                đầy đủ nhất nhé!
              </p>
            </div>
          </div>

          {/* Hero Section */}
          <div className='bg-white dark:bg-card-dark rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
            <div className='flex flex-col md:flex-row gap-6 md:items-start'>
              {/* Icon */}
              <div className='shrink-0'>
                <div className='h-32 w-32 md:h-40 md:w-40 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center border border-blue-200 dark:border-blue-800 text-primary'>
                  <CareerIcon className='w-16 h-16 md:w-20 md:h-20' />
                </div>
              </div>

              {/* Content */}
              <div className='flex flex-col flex-1 gap-4'>
                <div className='flex flex-col md:flex-row justify-between gap-4'>
                  <div>
                    <div className='flex items-center gap-2 mb-2 flex-wrap'>
                      <span className='bg-blue-100 dark:bg-blue-900/50 text-primary text-xs font-bold px-2 py-1 rounded uppercase tracking-wider'>
                        Mã ngành: {career.code}
                      </span>
                      <span className='bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 text-xs font-bold px-2 py-1 rounded flex items-center gap-1'>
                        <TrendingUp className='w-3 h-3' />
                        Hot Trend
                      </span>
                    </div>
                    <h1 className='text-3xl md:text-4xl font-bold leading-tight mb-2 text-gray-900 dark:text-white'>
                      {career.name}
                    </h1>
                    <p className='text-gray-600 dark:text-gray-400 text-lg'>
                      Nhóm ngành {career.category} • Khối thi {career.examBlocks}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsSaved(!isSaved)}
                    className={`flex items-center justify-center gap-2 h-11 px-6 ${isSaved ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-blue-600'} text-white text-sm font-bold rounded-lg transition-all shadow-md active:scale-95 whitespace-nowrap`}
                  >
                    <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                    <span>{isSaved ? 'Đã lưu' : 'Lưu ngành này'}</span>
                  </button>
                </div>

                {/* Quick Stats */}
                <div className='flex flex-wrap gap-4 md:gap-8 mt-2 pt-4 border-t border-gray-100 dark:border-gray-800'>
                  <div className='flex items-center gap-2'>
                    <div className='p-2 bg-green-50 dark:bg-green-900/20 rounded-full text-green-600'>
                      <Briefcase className='w-5 h-5' />
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold'>
                        Mức lương TB
                      </span>
                      <span className='text-sm font-bold text-gray-900 dark:text-white'>
                        {career.salary}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='p-2 bg-purple-50 dark:bg-purple-900/20 rounded-full text-purple-600'>
                      <Brain className='w-5 h-5' />
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold'>
                        Nhóm RIASEC
                      </span>
                      <span className='text-sm font-bold text-gray-900 dark:text-white'>
                        {career.riasec}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='p-2 bg-orange-50 dark:bg-orange-900/20 rounded-full text-orange-600'>
                      <Clock className='w-5 h-5' />
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold'>
                        Tỷ lệ việc làm
                      </span>
                      <span className='text-sm font-bold text-gray-900 dark:text-white'>
                        {career.employmentRate}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Layout Grid */}
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 items-start'>
            {/* Main Content */}
            <div className='lg:col-span-8 flex flex-col gap-6'>
              {/* Overview Section */}
              <section className='bg-white dark:bg-card-dark rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center gap-3 mb-4'>
                  <div className='p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-primary'>
                    <BookOpen className='w-5 h-5' />
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                    Tổng quan về ngành
                  </h3>
                </div>
                <div className='text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line'>
                  {career.overview}
                </div>
              </section>

              {/* Skills Section */}
              <section className='bg-white dark:bg-card-dark rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center gap-3 mb-6'>
                  <div className='p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-primary'>
                    <GraduationCap className='w-5 h-5' />
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                    Kỹ năng & Kiến thức cần thiết
                  </h3>
                </div>
                <div className='grid md:grid-cols-2 gap-8'>
                  <div className='flex flex-col gap-6'>
                    <div>
                      <h4 className='text-sm font-bold uppercase text-gray-500 dark:text-gray-400 mb-3 tracking-wide'>
                        Môn học phổ thông quan trọng
                      </h4>
                      <div className='flex flex-wrap gap-2'>
                        {career.subjects.map((subject, i) => (
                          <span
                            key={i}
                            className='px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded font-medium text-sm'
                          >
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className='text-sm font-bold uppercase text-gray-500 dark:text-gray-400 mb-3 tracking-wide'>
                        Kỹ năng mềm (Soft Skills)
                      </h4>
                      <ul className='space-y-2'>
                        {career.softSkills.map((skill, i) => (
                          <li
                            key={i}
                            className='flex items-start gap-2 text-gray-700 dark:text-gray-300 text-sm'
                          >
                            <CheckCircle className='w-5 h-5 text-green-500 mt-0.5 shrink-0' />
                            <span>{skill}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className='bg-primary/5 dark:bg-primary/10 rounded-lg p-5 border border-primary/10'>
                    <h4 className='text-sm font-bold uppercase text-primary mb-3 tracking-wide flex items-center gap-2'>
                      <Brain className='w-4 h-4' />
                      Tố chất phù hợp
                    </h4>
                    <p className='text-sm text-gray-700 dark:text-gray-300 mb-4'>
                      Bạn có thể phù hợp với ngành này nếu bạn:
                    </p>
                    <ul className='space-y-3'>
                      {career.traits.map((trait, i) => (
                        <li key={i} className='flex gap-3 items-start'>
                          <div className='h-1.5 w-1.5 rounded-full bg-primary mt-2 shrink-0' />
                          <p className='text-sm text-gray-700 dark:text-gray-300'>{trait}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Jobs Section */}
              <section className='bg-white dark:bg-card-dark rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center justify-between mb-6'>
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-primary'>
                      <Briefcase className='w-5 h-5' />
                    </div>
                    <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                      Cơ hội nghề nghiệp
                    </h3>
                  </div>
                  <span className='hidden sm:flex bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold px-3 py-1.5 rounded-full items-center gap-1'>
                    <TrendingUp className='w-4 h-4' />
                    Nhu cầu rất cao
                  </span>
                </div>
                <div className='space-y-4'>
                  {career.jobs.map((job, i) => {
                    const JobIcon = iconMap[job.icon] || Code;
                    return (
                      <div
                        key={i}
                        className='group relative flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all'
                      >
                        <div className='h-12 w-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0'>
                          <JobIcon className='w-6 h-6 text-gray-600 dark:text-gray-400' />
                        </div>
                        <div className='flex-1'>
                          <div className='flex justify-between items-start'>
                            <h4 className='font-bold text-gray-900 dark:text-white text-lg'>
                              {job.title}
                            </h4>
                            {job.hot && (
                              <span className='bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide'>
                                Hot
                              </span>
                            )}
                          </div>
                          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                            {job.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Universities Section */}
              <section className='bg-white dark:bg-card-dark rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center gap-3 mb-6'>
                  <div className='p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-primary'>
                    <Building2 className='w-5 h-5' />
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                    Trường đại học liên quan
                  </h3>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {career.universities.map((uni, i) => (
                    <a
                      key={i}
                      href='#'
                      className='flex items-center gap-4 p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors'
                    >
                      <div className='h-14 w-14 shrink-0 rounded bg-white dark:bg-gray-800 p-1 border border-gray-100 dark:border-gray-700 flex items-center justify-center'>
                        <span className='font-bold text-gray-400 text-xs'>{uni.abbr}</span>
                      </div>
                      <div>
                        <h4 className='text-sm font-bold text-gray-900 dark:text-white'>
                          {uni.name}
                        </h4>
                        <p className='text-xs text-gray-500'>
                          {uni.region} • {uni.note}
                        </p>
                      </div>
                      <ExternalLink className='w-4 h-4 ml-auto text-gray-400' />
                    </a>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className='lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-24'>
              {/* Related Careers */}
              <div className='bg-white dark:bg-card-dark rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700'>
                <h3 className='text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-between'>
                  Ngành nghề liên quan
                </h3>
                <div className='flex flex-col gap-3'>
                  {career.relatedCareers.map((rc, i) => {
                    const RcIcon = iconMap[rc.icon] || Terminal;
                    const colorClasses: Record<string, string> = {
                      indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600',
                      teal: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600',
                      pink: 'bg-pink-50 dark:bg-pink-900/20 text-pink-600',
                    };
                    return (
                      <Link
                        key={i}
                        to={`/career-exploration?career=${generateSlug(rc.name)}`}
                        className='group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
                      >
                        <div
                          className={`w-10 h-10 rounded flex items-center justify-center ${colorClasses[rc.color] || colorClasses['indigo']}`}
                        >
                          <RcIcon className='w-5 h-5' />
                        </div>
                        <div className='flex flex-col'>
                          <span className='text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary'>
                            {rc.name}
                          </span>
                          <span className='text-xs text-gray-500'>{rc.category}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Resource Card */}
              <div className='bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-5 shadow-lg text-white'>
                <div className='flex items-start justify-between mb-4'>
                  <h3 className='text-base font-bold text-white'>Tài liệu hướng nghiệp</h3>
                  <BookOpen className='w-5 h-5 text-white/80' />
                </div>
                <p className='text-sm text-blue-100 mb-4 leading-relaxed'>
                  Tải xuống cẩm nang chi tiết về ngành {career.name} để hiểu rõ lộ trình học tập và
                  phát triển.
                </p>
                <button className='w-full py-2 bg-white text-primary text-sm font-bold rounded shadow-sm hover:bg-blue-50 transition-colors flex items-center justify-center gap-2'>
                  <Download className='w-4 h-4' />
                  Tải Cẩm nang (PDF)
                </button>
              </div>

              {/* Success Story */}
              <div className='bg-yellow-50 dark:bg-yellow-900/10 rounded-xl p-5 border border-yellow-100 dark:border-yellow-900/30'>
                <div className='flex gap-1 text-yellow-500 mb-2'>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} className='w-4 h-4 fill-current' />
                  ))}
                </div>
                <p className='text-sm italic text-gray-900 dark:text-gray-300 mb-4'>
                  "Ngành CNTT không chỉ là viết code, mà là cách chúng ta dùng công nghệ để giải
                  quyết các vấn đề thực tế của cuộc sống. Cơ hội là vô tận nếu bạn đam mê."
                </p>
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-bold'>
                    NA
                  </div>
                  <div>
                    <p className='text-xs font-bold text-gray-900 dark:text-white'>Nguyễn Văn A</p>
                    <p className='text-[10px] text-gray-500 uppercase'>CTO tại TechCompany</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <UnifiedFooter />
    </div>
  );
}
